// Package-preview layout data — the data-driven replacement for the old
// 7,500-line AbsolutePosImgs.jsx. A "layout" describes how one package option's
// preview is composited: up to 16 absolutely-positioned slots, each layering a
// background, the student's pose, and a watermark.
//
// Each slot is either `null` (hidden — not rendered) or:
//   { top, left, width, pose, background }
//     top/left/width : CSS percentages, canvas-relative (resolution-independent)
//     pose           : which pose ROLE fills the slot (see POSE_ROLES below);
//                      resolved to a real image URL at render time
//     background     : a background TAG (see BG_SUBSTRINGS) or null (no bg layer)
//
// Pose roles (resolved in AbsolutePosImgs from the student's live poses):
//   pose1                  the student's first pose (deterministic)
//   pose2 / pose3 / pose4  a random "vertical" pose (for pkg 610, pose2/pose3
//                          exclude spring poses) — EXCEPT on a sport pricelist,
//                          where they resolve in curated order instead, so the
//                          sports layouts always draw -01 and -02
//   poseHoriz              a horizontal/formal pose found by URL scan
//   springpose1 / springpose2  pkg-610-only: random vertical pose excluding "-e0"
//
// These tables were generated from the legacy component and verified to
// reproduce its layout choice for all 2,322 real package options
// (see packageLayouts.test.js). Edit a layout's slots to adjust a preview.

// Background TAG -> the substring matched against the school's background set
// (getBackgroundUrl in AbsolutePosImgs scans backgrounds[].imageUrl for it).
export const BG_SUBSTRINGS = {
  blueBg: 'Senior_Blue',
  redBg: 'Senior_Red',
  whiteBg: 'Senior White',
  mochaBg: 'gradmocha',
  greenBg: 'Senior_Green',
  archwayBg: 'Archway',
  leavesBg: 'Leaves_Depth',
  brownBg: 'rown',
  grayBg: 'Gray',
  tealBg: 'Teal',
  concreteBg: 'oncrete',
  springBirch: 'irch',
  springOrchard: 'Orchard',
  fallleaves: 'all',
  chalkboardBg: 'halk2024',
  classroomBg: 'lassroom',
  flowerfieldBg: 'lowerfield',
  springstairsBg: 'springstairs',
  springarchBg: 'SpringArch',
};

export const LAYOUTS = {
  // scholarsChoice — e.g. 6 "B - Scholar" (28 options)
  scholarsChoice: [
    {
      top: '17%', left: '3%', width: '32%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '32%', left: '38%', width: '26%', pose: 'pose2', background: 'leavesBg',
    },
    {
      top: '10%', left: '67%', width: '12%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '10%', left: '88%', width: '9%', pose: 'pose4', background: 'greenBg',
    },
    {
      top: '50%', left: '67%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '54%', left: '86%', width: '6%', pose: 'pose2', background: 'mochaBg',
    },
    {
      top: '54%', left: '92%', width: '6%', pose: 'pose2', background: 'mochaBg',
    },
    {
      top: '66%', left: '86%', width: '6%', pose: 'pose2', background: 'mochaBg',
    },
    {
      top: '66%', left: '92%', width: '6%', pose: 'pose2', background: 'mochaBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // economySelection — e.g. 532 "Economy Selection" (21 options)
  economySelection: [
    {
      top: '35%', left: '4%', width: '18%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '35%', left: '21%', width: '18%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '40%', left: '42%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '40%', left: '49%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '40%', left: '56%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '40%', left: '64%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '42%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '49%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '56%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '64%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '24%', left: '75%', width: '12%', pose: 'pose3', background: 'leavesBg',
    },
    {
      top: '24%', left: '86%', width: '12%', pose: 'pose3', background: 'leavesBg',
    },
    {
      top: '48%', left: '75%', width: '12%', pose: 'pose3', background: 'leavesBg',
    },
    {
      top: '48%', left: '86%', width: '12%', pose: 'pose3', background: 'leavesBg',
    },
    null,
    null,
  ],
  // seniorSpecial — e.g. 532 "Senior Special" (21 options)
  seniorSpecial: [
    {
      top: '7%', left: '4%', width: '25%', pose: 'pose2', background: 'concreteBg',
    },
    {
      top: '7%', left: '31%', width: '25%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '60%', left: '14%', width: '13%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '53%', left: '81%', width: '15%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '49%', left: '63%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '8%', left: '60%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '8%', left: '67%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '8%', left: '74%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '8%', left: '81%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '22%', left: '60%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '22%', left: '67%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '22%', left: '74%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '22%', left: '81%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '60%', left: '26%', width: '13%', pose: 'pose3', background: 'brownBg',
    },
    null,
    null,
  ],
  // standardPackage — e.g. 532 "Standard Package" (21 options)
  standardPackage: [
    {
      top: '27%', left: '10%', width: '24%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '12%', left: '40%', width: '16%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '12%', left: '55%', width: '16%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '50%', left: '42%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '50%', left: '49%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '50%', left: '55%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '50%', left: '62%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '64%', left: '42%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '64%', left: '49%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '64%', left: '55%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '64%', left: '62%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '30%', left: '72%', width: '12%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '30%', left: '84%', width: '12%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '53%', left: '72%', width: '12%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '53%', left: '84%', width: '12%', pose: 'pose4', background: 'leavesBg',
    },
    null,
  ],
  // ultimateCollection — e.g. 532 "Ultimate Collection" (24 options)
  ultimateCollection: [
    {
      top: '16%', left: '3%', width: '32%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '42%', left: '40%', width: '20%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '42%', left: '63%', width: '8%', pose: 'pose3', background: 'redBg',
    },
    {
      top: '63%', left: '63%', width: '8%', pose: 'pose3', background: 'redBg',
    },
    {
      top: '9%', left: '69%', width: '14%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '9%', left: '83%', width: '14%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '55%', left: '73%', width: '10%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '53%', left: '86%', width: '6%', pose: 'pose2', background: 'greenBg',
    },
    {
      top: '53%', left: '93%', width: '6%', pose: 'pose2', background: 'greenBg',
    },
    {
      top: '69%', left: '86%', width: '6%', pose: 'pose2', background: 'greenBg',
    },
    {
      top: '69%', left: '93%', width: '6%', pose: 'pose2', background: 'greenBg',
    },
    null,
    null,
    null,
    null,
    null,
  ],
  // starStudent — e.g. 549 "All Star" (3 options)
  starStudent: [
    {
      top: '28%', left: '7%', width: '25%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '19%', left: '36%', width: '15%', pose: 'pose2', background: 'chalkboardBg',
    },
    {
      top: '19%', left: '52%', width: '15%', pose: 'pose2', background: 'chalkboardBg',
    },
    {
      top: '61%', left: '35%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '61%', left: '42%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '61%', left: '49%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '61%', left: '56%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '77%', left: '35%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '77%', left: '42%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '77%', left: '49%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '77%', left: '56%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '67%', left: '68%', width: '10%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '54%', left: '82%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    null,
    null,
    null,
  ],
  // p550BasicPack — e.g. 550 "Basic Pack" (1 option)
  p550BasicPack: [
    {
      top: '12%', left: '25%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '12%', left: '32%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '12%', left: '39%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '12%', left: '46%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '29%', left: '25%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '29%', left: '32%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '29%', left: '39%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '29%', left: '46%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '53%', left: '19%', width: '17%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '53%', left: '35%', width: '17%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '40%', left: '57%', width: '11%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '40%', left: '68%', width: '11%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '64%', left: '57%', width: '11%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '64%', left: '68%', width: '11%', pose: 'pose1', background: 'classroomBg',
    },
    null,
    null,
  ],
  // memories — e.g. 550 "Memories for Everyone" (3 options)
  memories: [
    {
      top: '19%', left: '10%', width: '15%', pose: 'pose2', background: 'fallleaves',
    },
    {
      top: '19%', left: '25%', width: '15%', pose: 'pose2', background: 'fallleaves',
    },
    {
      top: '71%', left: '6%', width: '10%', pose: 'pose1', background: 'mochaBg',
    },
    {
      top: '61%', left: '18%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '61%', left: '25%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '61%', left: '31%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '61%', left: '38%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '77%', left: '18%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '77%', left: '25%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '77%', left: '31%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '77%', left: '38%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '35%', left: '51%', width: '25%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '53%', left: '82%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    null,
    null,
    null,
  ],
  // justAn8x10 — e.g. 564 "Just an 8x10" (49 options)
  justAn8x10: [
    {
      top: '19%', left: '35%', width: '30%', pose: 'pose2', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // basicPackage — e.g. 579 "Basic Package" (25 options)
  basicPackage: [
    {
      top: '29.4%', left: '14.3%', width: '21.7%', pose: 'pose1', background: null,
    },
    {
      top: '29.4%', left: '36.9%', width: '10.7%', pose: 'pose1', background: null,
    },
    {
      top: '51.3%', left: '36.9%', width: '10.7%', pose: 'pose1', background: null,
    },
    {
      top: '43.1%', left: '54.6%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '43.1%', left: '62.1%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '43.1%', left: '69.5%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '43.1%', left: '76.8%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '57.9%', left: '54.6%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '57.9%', left: '62.1%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '57.9%', left: '69.5%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '57.9%', left: '76.8%', width: '7.1%', pose: 'pose2', background: null,
    },
    null,
    null,
    null,
    null,
    null,
  ],
  // picturePerfect — e.g. 584 "Picture Perfect Package" (24 options)
  picturePerfect: [
    {
      top: '4.8%', left: '5.1%', width: '29.2%', pose: 'pose2', background: null,
    },
    {
      top: '7.7%', left: '37%', width: '11.5%', pose: 'pose1', background: null,
    },
    {
      top: '7.7%', left: '48.6%', width: '11.5%', pose: 'pose1', background: null,
    },
    {
      top: '31%', left: '37%', width: '11.5%', pose: 'pose1', background: null,
    },
    {
      top: '31%', left: '48.6%', width: '11.5%', pose: 'pose1', background: null,
    },
    {
      top: '66.3%', left: '5.2%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '66.3%', left: '12.8%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '66.3%', left: '20.3%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '66.3%', left: '27.7%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '81%', left: '5.2%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '81%', left: '12.8%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '81%', left: '20.3%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '81%', left: '27.7%', width: '7%', pose: 'pose3', background: null,
    },
    // poseHoriz: the landscape class photo (pose 25) fills the wide chalkboard slot.
    // Renders nothing when the student has no landscape class photo (see AbsolutePosImgs).
    {
      top: '64%', left: '37.9%', width: '24.1%', pose: 'poseHoriz', background: null,
    },
    {
      top: '61.6%', left: '68.4%', width: '12.1%', pose: 'pose2', background: null,
    },
    null,
  ],
  // schoolDays — e.g. 584 "School Days Package" (24 options)
  schoolDays: [
    {
      top: '25.8%', left: '7.2%', width: '29.1%', pose: 'pose4', background: null,
    },
    {
      top: '43%', left: '41.9%', width: '20%', pose: 'pose2', background: null,
    },
    {
      top: '49.9%', left: '67.6%', width: '12.4%', pose: 'pose3', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p610BasicPack — e.g. 610 "Basic Pack" (1 option)
  p610BasicPack: [
    {
      top: '29%', left: '10%', width: '23%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '41%', left: '41%', width: '13%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '35%', left: '59%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '35%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '35%', left: '77%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '35%', left: '86%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '59%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '77%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '86%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p610DigitalAllTheWay — e.g. 610 "Digital All the Way" (1 option)
  p610DigitalAllTheWay: [
    {
      top: '13%', left: '14%', width: '8%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '13%', left: '23%', width: '8%', pose: 'pose3', background: 'grayBg',
    },
    {
      top: '33%', left: '14%', width: '8%', pose: 'pose3', background: 'chalkboardBg',
    },
    {
      top: '33%', left: '23%', width: '8%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '53%', left: '14%', width: '8%', pose: 'springpose2', background: 'flowerfieldBg',
    },
    {
      top: '53%', left: '23%', width: '8%', pose: 'springpose1', background: 'springBirch',
    },
    {
      top: '73%', left: '14%', width: '8%', pose: 'springpose1', background: 'springstairsBg',
    },
    {
      top: '73%', left: '23%', width: '8%', pose: 'springpose2', background: 'springarchBg',
    },
    {
      top: '16%', left: '39%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '16%', left: '53%', width: '12%', pose: 'pose3', background: 'grayBg',
    },
    {
      top: '16%', left: '66%', width: '12%', pose: 'pose3', background: 'chalkboardBg',
    },
    {
      top: '16%', left: '80%', width: '12%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '62%', left: '39%', width: '12%', pose: 'springpose2', background: 'flowerfieldBg',
    },
    {
      top: '62%', left: '53%', width: '12%', pose: 'springpose1', background: 'springBirch',
    },
    {
      top: '62%', left: '66%', width: '12%', pose: 'springpose1', background: 'springstairsBg',
    },
    {
      top: '62%', left: '80%', width: '12%', pose: 'springpose2', background: 'springarchBg',
    },
  ],
  // p610DoubleDouble — e.g. 610 "Double Double" (1 option)
  p610DoubleDouble: [
    {
      top: '11%', left: '5%', width: '32%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '15%', left: '39%', width: '32%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '19%', left: '80%', width: '9%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '41%', left: '80%', width: '9%', pose: 'springpose2', background: 'springOrchard',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p610JustADigital — e.g. 610 "Just a Digital" (1 option)
  p610JustADigital: [
    {
      top: '28%', left: '12%', width: '20%', pose: 'pose2', background: 'blueBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p610PerfectPack2Digitals — e.g. 610 "Perfect Pack with 2 Digitals" (1 option)
  p610PerfectPack2Digitals: [
    {
      top: '13%', left: '6%', width: '35%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '7%', left: '44%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '52%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '60%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '44%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '52%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '60%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '13%', left: '82%', width: '13%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '49%', left: '47%', width: '7%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '67%', left: '47%', width: '7%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '49%', left: '64%', width: '15%', pose: 'springpose2', background: 'springOrchard',
    },
    {
      top: '49%', left: '77%', width: '15%', pose: 'springpose2', background: 'springOrchard',
    },
    null,
    null,
  ],
  // p610SomethingForEveryone — e.g. 610 "Something for Everyone" (1 option)
  p610SomethingForEveryone: [
    {
      top: '15%', left: '6%', width: '34%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '7%', left: '47%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '54%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '61%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '47%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '54%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '61%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '18%', left: '84%', width: '12%', pose: 'springpose2', background: 'springOrchard',
    },
    {
      top: '55%', left: '46%', width: '4%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '55%', left: '51%', width: '4%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '66%', left: '46%', width: '4%', pose: 'springpose2', background: 'springOrchard',
    },
    {
      top: '66%', left: '51%', width: '4%', pose: 'springpose2', background: 'springOrchard',
    },
    {
      top: '52%', left: '63%', width: '15%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '52%', left: '79%', width: '15%', pose: 'pose3', background: 'blueBg',
    },
  ],
  // p611Two8x10Plus — e.g. 611 "Two 8x10’s Plus" (1 option)
  p611Two8x10Plus: [
    {
      top: '13%', left: '33%', width: '34%', pose: 'pose1', background: 'blueBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p616Two8x10Plus — e.g. 616 "Two 8x10’s Plus" (1 option)
  p616Two8x10Plus: [
    {
      top: '12%', left: '33%', width: '35%', pose: 'pose1', background: 'blueBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p629DigitalAllTheWay — e.g. 629 "Digital All the Way" (1 option)
  p629DigitalAllTheWay: [
    {
      top: '32%', left: '6%', width: '19%', pose: 'pose2', background: null,
    },
    {
      top: '11%', left: '32%', width: '38%', pose: 'pose1', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p629Memories — e.g. 629 "Memories Package" (1 option)
  p629Memories: [
    {
      top: '12%', left: '3%', width: '23%', pose: 'pose2', background: null,
    },
    {
      top: '37%', left: '27%', width: '23%', pose: 'pose2', background: null,
    },
    {
      top: '16%', left: '52%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '16%', left: '60%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '34%', left: '52%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '34%', left: '60%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '55%', left: '52%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '55%', left: '60%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '73%', left: '52%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '73%', left: '60%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '13%', left: '69%', width: '28%', pose: 'pose1', background: null,
    },
    {
      top: '60%', left: '74%', width: '10%', pose: 'pose1', background: null,
    },
    {
      top: '60%', left: '83%', width: '10%', pose: 'pose1', background: null,
    },
    {
      top: '74%', left: '74%', width: '10%', pose: 'pose1', background: null,
    },
    {
      top: '74%', left: '83%', width: '10%', pose: 'pose1', background: null,
    },
    null,
  ],
  // p629StarStudent — e.g. 629 "Star Student Package" (1 option)
  p629StarStudent: [
    {
      top: '12%', left: '6%', width: '25%', pose: 'pose2', background: null,
    },
    {
      top: '77%', left: '7%', width: '9%', pose: 'pose2', background: null,
    },
    {
      top: '77%', left: '20%', width: '9%', pose: 'pose2', background: null,
    },
    {
      top: '11%', left: '35%', width: '14%', pose: 'pose2', background: null,
    },
    {
      top: '11%', left: '49%', width: '14%', pose: 'pose2', background: null,
    },
    {
      top: '50%', left: '35%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '50%', left: '41%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '50%', left: '48%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '50%', left: '55%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '66%', left: '35%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '66%', left: '41%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '66%', left: '48%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '66%', left: '55%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '8%', left: '65%', width: '31%', pose: 'pose1', background: null,
    },
    {
      top: '68%', left: '66%', width: '11%', pose: 'pose1', background: null,
    },
    {
      top: '59%', left: '79%', width: '18%', pose: 'pose1', background: null,
    },
  ],
  // allDigital — e.g. 642 "All Digital Package" (15 options)
  allDigital: [
    {
      top: '10.7%', left: '6.1%', width: '17%', pose: 'pose4', background: null,
    },
    {
      top: '10.7%', left: '25%', width: '17%', pose: 'pose2', background: null,
    },
    // poseHoriz: the landscape class photo (pose 25) fills the wide chalkboard slot.
    // Renders nothing when the student has no landscape class photo (see AbsolutePosImgs).
    {
      top: '48.1%', left: '6.1%', width: '21%', pose: 'poseHoriz', background: null,
    },
    {
      top: '48.1%', left: '29%', width: '13.2%', pose: 'pose3', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // giftsForEveryone — e.g. 642 "Gifts for Everyone Package" (16 options)
  giftsForEveryone: [
    {
      top: '10.4%', left: '10.3%', width: '18%', pose: 'pose4', background: null,
    },
    {
      top: '10.4%', left: '28.5%', width: '18%', pose: 'pose4', background: null,
    },
    {
      top: '8.5%', left: '56.5%', width: '13%', pose: 'pose2', background: null,
    },
    {
      top: '7.8%', left: '76%', width: '13%', pose: 'pose2', background: null,
    },
    {
      top: '53.6%', left: '10.1%', width: '18%', pose: 'pose3', background: null,
    },
    {
      top: '53.6%', left: '28.5%', width: '18%', pose: 'pose3', background: null,
    },
    {
      top: '41.8%', left: '55%', width: '16.5%', pose: 'pose4', background: null,
    },
    {
      top: '50.1%', left: '73.4%', width: '16.5%', pose: 'pose4', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // starStudent2027 — 877 "EL Fall 2027" / 778 "JH-HS Fall 2027" "Star Student" (2026-27
  // top tier: Keepsake prints + all 12 downloads). Slots measured against the
  // designer's card art (options/images/.../Fall_Star_Student.png, 1050x660): the
  // front sheet of each stack — blue 8x10, two fall-tree 5x7s, the front chalkboard
  // 3x5 in the top-right stack, 8 classroom wallets, the phone screen. The lower
  // right is the "ALL DIGITAL" badge and carries no slot.
  starStudent2027: [
    {
      top: '29.5%', left: '6.9%', width: '25.3%', pose: 'pose1', background: null,
    },
    {
      top: '22.4%', left: '36.2%', width: '15.2%', pose: 'pose2', background: null,
    },
    {
      top: '22.4%', left: '51.4%', width: '15.2%', pose: 'pose2', background: null,
    },
    {
      top: '24%', left: '77.5%', width: '14%', pose: 'pose3', background: null,
    },
    {
      top: '58.8%', left: '35.4%', width: '6.5%', pose: 'pose1', background: null,
    },
    {
      top: '58.8%', left: '42.3%', width: '6.5%', pose: 'pose1', background: null,
    },
    {
      top: '58.8%', left: '49.2%', width: '6.5%', pose: 'pose1', background: null,
    },
    {
      top: '58.8%', left: '56.1%', width: '6.5%', pose: 'pose1', background: null,
    },
    {
      top: '72.4%', left: '35.4%', width: '6.5%', pose: 'pose1', background: null,
    },
    {
      top: '72.4%', left: '42.3%', width: '6.5%', pose: 'pose1', background: null,
    },
    {
      top: '72.4%', left: '49.2%', width: '6.5%', pose: 'pose1', background: null,
    },
    {
      top: '72.4%', left: '56.1%', width: '6.5%', pose: 'pose1', background: null,
    },
    {
      top: '60%', left: '65.7%', width: '9.5%', pose: 'pose2', background: null,
    },
    null,
    null,
    null,
  ],

  // ---------------------------------------------------------------------------
  // SPORTS — package 976 "Fall Sports 2026-2027v1" (season sportsfall2027).
  //
  // All seven sellable options whose art ships with empty print placeholders.
  // Measured with ImageMagick on the 2026-10-07 `_blank` art by labelling the
  // NON-WHITE HOLES between each sheet's white border (threshold 78% on the HSL
  // L channel), not by eye, and every box was then drawn back onto the art and
  // checked. Each slot came back 4:5 (prints) or 5:4 (team) to within a pixel,
  // so prints take the compositor's default aspect and only the phone pins its
  // own.
  //
  // ⚠️ RE-MEASURED, not inherited. The first cut of sportsSuperstar was measured
  // against `Sports_Superstar_Sample.png`; the reissued blank art MOVED the
  // sheets (the wallet block alone shifted ~41px left and ~40px up and grew),
  // so the old numbers misregistered every print. If the art is reissued again,
  // re-measure — do not assume the geometry carried over.
  //
  // ⚠️ TWO CANVAS SIZES. 3056/3061 are 940x600; the rest are 1200x646. The
  // layouts are canvas-relative percentages, so each is measured against its own
  // file — never copy a box between the two groups.
  //
  // POSE ROLES: a sports student gets two individual poses and a team photo, so
  // these layouts use pose1 (-01), pose2 (-02) and poseHoriz (the team picture,
  // -04) and nothing else. pose1/pose2 are the pricelist's first two curated
  // poses, resolved DETERMINISTICALLY for a sport pricelist — see the
  // `isSport` branch in AbsolutePosImgs. The team slot resolves to null for a
  // student with no landscape frame, and then simply renders nothing.
  //
  // ⚠️ THE PHONE SLOTS DELIBERATELY DO NOT USE frame: 'phone'. That helper is
  // for art with no bezel of its own and paints PHONE_FRAME_STYLE's #1c1c1e
  // over the slot. Every one of these arts draws its own phone, and the slot is
  // only the PHOTO area inside the screen (below the SHOOB logo header), so a
  // painted frame would put the student on a black rectangle. A test pins this.
  //
  // STACKS. Several of these sheets are drawn as a stack in the artwork, and a
  // slot carrying `stack` is expanded by stackSlivers (AbsolutePosImgs) into
  // the sheets behind it, so a stack reads as several copies of the student's
  // own print instead of one print on blank paper. `count` includes the front
  // sheet; `dx`/`dy` are the sheet pitch and are BOTH percent of canvas WIDTH
  // (there is no canvas height to measure against -- see that function);
  // `edges` names the stack edges this print touches, defaulting to 'LT'. On a
  // multi-print sheet only the top row and left column are ever visible behind,
  // so the interior wallets carry no stack at all. Measured by scanning for the
  // white border of each sheet, not by eye.

  sportsSuperstar: [
    // 8x10  (3 sheets)
    {
      top: '15.848%',
      left: '7.167%',
      width: '24.417%',
      pose: 'pose1',
      background: null,
      stack: { count: 3, dx: 0.7500, dy: 0.7083 },
    },
    // 5x7   (4 sheets)
    {
      top: '12.113%',
      left: '37.667%',
      width: '12.167%',
      pose: 'pose2',
      background: null,
      stack: { count: 4, dx: 0.7225, dy: 0.7225 },
    },
    // 3x5   (4 sheets)
    {
      top: '17.512%',
      left: '55.417%',
      width: '8.583%',
      pose: 'pose2',
      background: null,
      stack: { count: 4, dx: 0.7775, dy: 0.7775 },
    },
    // wallet r1c1
    {
      top: '46.517%',
      left: '37.75%',
      width: '6.333%',
      pose: 'pose1',
      background: null,
      stack: { count: 3, dx: 0.7083, dy: 0.7083 },
    },
    // wallet r1c2
    {
      top: '46.227%',
      left: '44.417%',
      width: '6.583%',
      pose: 'pose1',
      background: null,
      stack: {
        count: 3, dx: 0.7083, dy: 0.7083, edges: 'T',
      },
    },
    // wallet r1c3
    {
      top: '46.42%',
      left: '51.333%',
      width: '6.417%',
      pose: 'pose1',
      background: null,
      stack: {
        count: 3, dx: 0.7083, dy: 0.7083, edges: 'T',
      },
    },
    // wallet r1c4
    {
      top: '46.227%',
      left: '58.083%',
      width: '6.583%',
      pose: 'pose1',
      background: null,
      stack: {
        count: 3, dx: 0.7083, dy: 0.7083, edges: 'T',
      },
    },
    // wallet r2c1
    {
      top: '62.539%',
      left: '37.75%',
      width: '6.333%',
      pose: 'pose1',
      background: null,
      stack: {
        count: 3, dx: 0.7083, dy: 0.7083, edges: 'L',
      },
    },
    // wallet r2c2
    {
      top: '62.248%', left: '44.417%', width: '6.583%', pose: 'pose1', background: null,
    },
    // wallet r2c3
    {
      top: '62.442%', left: '51.333%', width: '6.417%', pose: 'pose1', background: null,
    },
    // wallet r2c4
    {
      top: '62.248%', left: '58.083%', width: '6.583%', pose: 'pose1', background: null,
    },
    // team picture (landscape)
    {
      top: '53.901%', left: '70.083%', width: '26.417%', pose: 'poseHoriz', background: null,
    },
    // phone screen (art draws its own bezel)
    {
      top: '19.195%', left: '70.417%', width: '10.167%', pose: 'pose1', background: null, aspectRatio: '122 / 151',
    },
    null,
    null,
    null,
  ],

  sports8x10: [
    // 8x10
    {
      top: '19.292%', left: '35%', width: '30%', pose: 'pose1', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],

  sportsBasic: [
    // 8x10
    {
      top: '29.396%', left: '14.362%', width: '21.809%', pose: 'pose1', background: null,
    },
    // 5x7 top
    {
      top: '29.396%', left: '36.809%', width: '10.745%', pose: 'pose2', background: null,
    },
    // 5x7 bottom
    {
      top: '51.229%', left: '36.809%', width: '10.745%', pose: 'pose2', background: null,
    },
    // wallet r1c1
    {
      top: '43.25%', left: '54.787%', width: '7.234%', pose: 'pose1', background: null,
    },
    // wallet r1c2
    {
      top: '43.25%', left: '62.128%', width: '7.234%', pose: 'pose1', background: null,
    },
    // wallet r1c3
    {
      top: '43.25%', left: '69.468%', width: '7.234%', pose: 'pose1', background: null,
    },
    // wallet r1c4
    {
      top: '43.25%', left: '76.809%', width: '7.234%', pose: 'pose1', background: null,
    },
    // wallet r2c1
    {
      top: '57.917%', left: '54.787%', width: '7.234%', pose: 'pose1', background: null,
    },
    // wallet r2c2
    {
      top: '57.917%', left: '62.128%', width: '7.234%', pose: 'pose1', background: null,
    },
    // wallet r2c3
    {
      top: '57.917%', left: '69.468%', width: '7.234%', pose: 'pose1', background: null,
    },
    // wallet r2c4
    {
      top: '57.917%', left: '76.809%', width: '7.234%', pose: 'pose1', background: null,
    },
    null,
    null,
    null,
    null,
    null,
  ],

  sportsTeamPicture: [
    // team picture (landscape)
    {
      top: '19.474%', left: '29.917%', width: '41.083%', pose: 'poseHoriz', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],

  sportsAllDigital: [
    // phone screen (art draws its own bezel)
    {
      top: '26.935%', left: '16.167%', width: '16.667%', pose: 'pose1', background: null, aspectRatio: '200 / 250',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],

  sportsPicturePerfect: [
    // 5x7 left  (SAME SHEET as 5x7 right)
    {
      top: '5.805%', left: '7.5%', width: '12.667%', pose: 'pose2', background: null,
    },
    // 5x7 right (same sheet)
    {
      top: '5.902%', left: '20.333%', width: '12.583%', pose: 'pose2', background: null,
    },
    // 8x10
    {
      top: '18.208%', left: '39.833%', width: '24.917%', pose: 'pose1', background: null,
    },
    // wallet stack r1c1 (2 sheets)
    {
      top: '43.053%',
      left: '5.5%',
      width: '5.25%',
      pose: 'pose1',
      background: null,
      stack: { count: 2, dx: 1.1667, dy: 0.9167 },
    },
    // wallet stack r1c2 (2 sheets)
    {
      top: '43.247%',
      left: '10.917%',
      width: '5.083%',
      pose: 'pose1',
      background: null,
      stack: {
        count: 2, dx: 1.1667, dy: 0.9167, edges: 'T',
      },
    },
    // wallet stack r2c1
    {
      top: '55.592%',
      left: '5.5%',
      width: '5.25%',
      pose: 'pose1',
      background: null,
      stack: {
        count: 2, dx: 1.1667, dy: 0.9167, edges: 'L',
      },
    },
    // wallet stack r2c2
    {
      top: '55.786%', left: '10.917%', width: '5.083%', pose: 'pose1', background: null,
    },
    // wallet r1c1
    {
      top: '41.37%', left: '20.333%', width: '7.5%', pose: 'pose2', background: null,
    },
    // wallet r1c2
    {
      top: '41.563%', left: '28%', width: '7.333%', pose: 'pose2', background: null,
    },
    // wallet r2c1
    {
      top: '59.172%', left: '20.333%', width: '7.5%', pose: 'pose2', background: null,
    },
    // wallet r2c2
    {
      top: '59.365%', left: '28%', width: '7.333%', pose: 'pose2', background: null,
    },
    // team picture (landscape)
    {
      top: '53.901%', left: '70.083%', width: '26.417%', pose: 'poseHoriz', background: null,
    },
    // phone screen (art draws its own bezel)
    {
      top: '19.505%', left: '70.417%', width: '10.167%', pose: 'pose1', background: null, aspectRatio: '122 / 151',
    },
    null,
    null,
    null,
  ],

  sportsAllStar: [
    // 5x7 left  (SAME SHEET as 5x7 right)
    {
      top: '7.314%', left: '25.5%', width: '15.5%', pose: 'pose2', background: null,
    },
    // 5x7 right (same sheet)
    {
      top: '7.508%', left: '41.333%', width: '15.333%', pose: 'pose2', background: null,
    },
    // 8x10 stack left  (2 sheets)
    {
      top: '46.285%',
      left: '7.75%',
      width: '12.333%',
      pose: 'pose1',
      background: null,
      stack: { count: 2, dx: 1.4167, dy: 1.1667 },
    },
    // 8x10 stack right (2 sheets)
    {
      top: '50%',
      left: '24.083%',
      width: '12.333%',
      pose: 'pose2',
      background: null,
      stack: { count: 2, dx: 1.4167, dy: 1.1667 },
    },
    // wallet r1c1
    {
      top: '50.619%', left: '41.167%', width: '5.667%', pose: 'pose1', background: null,
    },
    // wallet r1c2
    {
      top: '50.522%', left: '47%', width: '5.75%', pose: 'pose1', background: null,
    },
    // wallet r1c3
    {
      top: '50.813%', left: '52.917%', width: '5.5%', pose: 'pose1', background: null,
    },
    // wallet r1c4
    {
      top: '50.522%', left: '58.583%', width: '5.75%', pose: 'pose1', background: null,
    },
    // wallet r2c1
    {
      top: '64.241%', left: '41.167%', width: '5.667%', pose: 'pose1', background: null,
    },
    // wallet r2c2
    {
      top: '64.145%', left: '47%', width: '5.75%', pose: 'pose1', background: null,
    },
    // wallet r2c3
    {
      top: '64.435%', left: '52.917%', width: '5.5%', pose: 'pose1', background: null,
    },
    // wallet r2c4
    {
      top: '64.145%', left: '58.583%', width: '5.75%', pose: 'pose1', background: null,
    },
    // team picture (landscape)
    {
      top: '53.901%', left: '70.083%', width: '26.417%', pose: 'poseHoriz', background: null,
    },
    // phone screen (art draws its own bezel)
    {
      top: '18.421%', left: '70.917%', width: '10.167%', pose: 'pose1', background: null, aspectRatio: '122 / 151',
    },
    null,
    null,
  ],

  // keepsake — e.g. 642 "The Keepsake Collection" (16 options)
  keepsake: [
    {
      top: '13.4%', left: '8.2%', width: '22.9%', pose: 'pose4', background: null,
    },
    {
      top: '14.5%', left: '41.0%', width: '13%', pose: 'pose2', background: null,
    },
    {
      top: '44.8%', left: '35%', width: '9.2%', pose: 'pose3', background: null,
    },
    {
      top: '45.3%', left: '46.6%', width: '9.2%', pose: 'pose3', background: null,
    },
    {
      top: '44.8%', left: '61.4%', width: '15.5%', pose: 'pose4', background: null,
    },
    {
      top: '52.6%', left: '78.4%', width: '15.5%', pose: 'pose4', background: null,
    },
    {
      top: '70%', left: '4.9%', width: '8.1%', pose: 'pose3', background: null,
    },
    {
      top: '72%', left: '33.2%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '72%', left: '38.6%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '72%', left: '44.4%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '72%', left: '50.1%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '83.1%', left: '33.2%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '83.1%', left: '38.6%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '83.1%', left: '44.4%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '83.1%', left: '50.1%', width: '5.2%', pose: 'pose2', background: null,
    },
    null,
  ],
};

// The elementary "Download all my photos" tile (issue #5218 item 6): the
// student composited into the banner art's two print placeholders. Not selected by
// selectLayout -- the gallery tile is not a pricelist option card -- so it is
// reached only by name (AbsolutePosImgs's `layoutKey` override). Slots are
// canvas-relative to the 4:5 tile.
export const BUY_ALL_TILE_LAYOUT = Object.freeze([
  // Measured against the 640x800 "Download All + 2 Magnets" banner art (the
  // tile is aspect-[4/5], so percentages map 1:1 to the art). The art bakes
  // EVERYTHING except the student: the blue print backgrounds, their white
  // borders and shadows, and the YOU SAVE roundel. Each slot is a bare
  // transparent-PNG cutout sized to its placeholder's blue area ONLY (left
  // x116-310 y309-555; right x314-498 y332-578). Still LIVE: the seniors
  // banner (option 2671 / package 745, "..._2_Magnets_Seniors") is drawn to
  // this geometry.
  {
    top: '38.6%',
    left: '18.1%',
    width: '30.3%',
    aspectRatio: '194 / 246',
    pose: 'pose1',
    background: null,
    frame: null,
  },
  {
    top: '41.5%',
    left: '49.1%',
    width: '28.75%',
    aspectRatio: '184 / 246',
    pose: 'pose2',
    background: null,
    frame: null,
  },
]);

export const BUY_ALL_BLANK_LAYOUT = Object.freeze([
  // Measured against the 640x800 "Download_All_Buy_Now_20 off_NoPrice_Blank"
  // art (tile is aspect-[4/5], so percentages map 1:1). The art bakes the
  // bokeh ground, phone bezel, print borders/shadows and all copy; every
  // placeholder is an empty backdrop swatch the compositor fills with a bare
  // transparent cutout. Two overlapping prints (grey x258-409 y201-393, blue
  // x439-590 y228-420) plus the phone's five tiles (4 portrait corners
  // 68x85, one landscape center 98x78 -> poseHoriz). A role that resolves to
  // no pose (few-pose students, no landscape) simply leaves the baked
  // backdrop visible -- graceful by construction.
  {
    top: '25.1%',
    left: '40.3%',
    width: '23.6%',
    aspectRatio: '151 / 192',
    pose: 'pose1',
    background: null,
    frame: null,
  },
  {
    top: '28.5%',
    left: '68.6%',
    width: '23.6%',
    aspectRatio: '151 / 192',
    pose: 'pose2',
    background: null,
    frame: null,
  },
  {
    top: '16.9%',
    left: '10.5%',
    width: '10.6%',
    aspectRatio: '68 / 85',
    pose: 'pose3',
    background: null,
    frame: null,
  },
  {
    top: '16.9%',
    left: '22.3%',
    width: '10.5%',
    aspectRatio: '68 / 85',
    pose: 'pose4',
    background: null,
    frame: null,
  },
  {
    top: '28.25%',
    left: '14.5%',
    width: '15.3%',
    aspectRatio: '98 / 78',
    pose: 'poseHoriz',
    background: null,
    frame: null,
  },
  {
    top: '38.75%',
    left: '10.5%',
    width: '10.6%',
    aspectRatio: '68 / 85',
    pose: 'pose1',
    background: null,
    frame: null,
  },
  {
    top: '38.75%',
    left: '22.2%',
    width: '10.6%',
    aspectRatio: '68 / 85',
    pose: 'pose2',
    background: null,
    frame: null,
  },
]);

// Whether a buy-all banner is art BUY_ALL_TILE_LAYOUT was measured against — i.e. art that
// leaves two empty print placeholders for the student to be composited into.
//
// The layout above is percentages tuned to ONE piece of art. Drawn over any other buy-all
// banner they land wherever those numbers happen to fall. On the past-images pricelists
// (option 2525 on 674, 2473/2499 on 672/673) that put two cutouts of the student straight
// across the baked "All Digital Package / Download All My Photos" line of
// `Download_All_Buy_Now_50_off.png`.
//
// Matching the ART, not the offer: the slots are a property of the file, and gating on the
// live free-magnets tier instead would blank them out the moment that tier lapsed while the
// magnet art was still attached.
//
// Two slot-art naming families are accepted (see slotLayoutKeyForArt below):
// the `2_Magnets` family (two-print geometry; still live on the seniors
// banner, option 2671) and the `..._Blank.png` suffix series (seven slots).
// Unknown art fails SAFE -- no composite; the banner renders exactly as
// drawn, which is what every self-contained banner wants. A NEW slot banner
// must use one of these tokens, and its geometry must match the layout that
// token routes to; there is no flag on the option to say otherwise.
const MAGNET_SLOT_ART = /2_magnets/i;
const BLANK_SLOT_ART = /_blank\.(png|jpe?g)(\?|$)/i;

// Which named layout a buy-all banner's placeholders were drawn for, by
// filename convention: the '2_Magnets' family (two prints,
// BUY_ALL_TILE_LAYOUT -- still live on the seniors banner, option 2671) vs
// the '_Blank' placeholder series -- matched as the FILENAME SUFFIX
// "..._Blank.png" (never a bare substring: "Blanket_Style" art must stay
// bare) -- (two prints + five phone tiles,
// BUY_ALL_BLANK_LAYOUT). Null = self-contained art; the card renders bare.
export function slotLayoutKeyForArt(artUrl) {
  const url = String(artUrl || '');
  if (BLANK_SLOT_ART.test(url)) return 'buyAllBlank';
  if (MAGNET_SLOT_ART.test(url)) return 'buyAllTile';
  return null;
}

export function artHasCompositeSlots(artUrl) {
  return slotLayoutKeyForArt(artUrl) !== null;
}

// A named layout looked up by key; null when the key is unknown (the caller
// simply renders no composite).
export function namedLayoutFor(layoutKey, namedLayouts) {
  return namedLayouts[layoutKey] || null;
}

// Choose the layout for a package option. Ordered, first-match-wins — mirrors the
// legacy if-chain precedence. Package-specific rules come before the general
// name-based ones (e.g. 629 and 610 reuse common option names with their own
// layouts). Returns a key of LAYOUTS, or null when the option has no preview
// layout (the preview simply isn't shown). Verified against all real options in
// packageLayouts.test.js — keep that test green when editing these rules.
export function selectLayout(packageId, optionName) {
  const pkg = Number(packageId); // packageId may arrive as a string; legacy used loose ==
  const name = optionName || '';
  const has = (s) => name.includes(s);

  // Package 610 — its own set of named layouts
  if (pkg === 610) {
    if (has('Basic Pack')) return 'p610BasicPack';
    if (has('Digital All the Way')) return 'p610DigitalAllTheWay';
    if (has('Double Double')) return 'p610DoubleDouble';
    if (has('Just a Digital')) return 'p610JustADigital';
    if (has('Perfect Pack with 2 Digitals')) return 'p610PerfectPack2Digitals';
    if (has('Something for Everyone')) return 'p610SomethingForEveryone';
    // else falls through to the general rules (e.g. 610 "Just an 8x10")
  }

  // Package 629 — reuses common names with its own layouts
  if (pkg === 629) {
    if (has('Digital All the Way')) return 'p629DigitalAllTheWay';
    if (has('Memories')) return 'p629Memories';
    if (has('Star Student')) return 'p629StarStudent';
  }

  // Package-scoped layouts that share a name with the general ones
  // 2026-27 fall pricelists: the new top tier reuses the old "Star Student" name.
  // Scoped, not general -- several retired packages carry that option name with
  // no preview (584, 621, 625, 626, 639 in the fixture) and must stay that way.
  if ([877, 778].includes(pkg) && has('Star Student')) return 'starStudent2027';
  // 976 "Fall Sports 2026-2027v1" — every rule here is package-scoped, because
  // these option names ("Superstar", "Basic", "All Star", "Picture Perfect…")
  // are names half the sports pricelists reuse with DIFFERENT art (628, 580,
  // 622, 649, 656, 664 …). A general rule would point every one of them at
  // geometry measured against 976's files alone.
  //
  // ⚠️ selectLayout is NOT art-gated. `heroOption` checks artHasCompositeSlots
  // before using a layout on the picker card, but the package BUILDER draws an
  // option preview from selectLayout alone. So a rule here must only ever match
  // an option whose art actually ships with empty placeholders. On 976 that is
  // these seven; "Digital Value Pack" (3055), "Just A Digital" (3058) and
  // "Youth Sports Options" (3057) keep self-contained art and must stay bare.
  if (pkg === 976) {
    if (has('Superstar')) return 'sportsSuperstar';
    if (has('All Star')) return 'sportsAllStar';
    if (has('Picture Perfect')) return 'sportsPicturePerfect';
    if (has('Team Picture Only')) return 'sportsTeamPicture';
    if (has('The Digital Package')) return 'sportsAllDigital';
    if (has('Only an 8x10')) return 'sports8x10';
    if (has('Basic')) return 'sportsBasic';
  }
  if (pkg === 611 && has('Two 8x10')) return 'p611Two8x10Plus';
  if (pkg === 616 && has('Two 8x10')) return 'p616Two8x10Plus';
  if (pkg === 550 && has('Basic Pack')) return 'p550BasicPack';
  if ([549, 611, 616].includes(pkg) && has('Star')) return 'starStudent';
  if ([550, 611, 616].includes(pkg) && has('Memories')) return 'memories';

  // General, name-based layouts (package-independent)
  if (has('Scholar')) return 'scholarsChoice';
  if (has('Economy Selection')) return 'economySelection';
  if (has('Senior Special')) return 'seniorSpecial';
  if (has('Standard Package')) return 'standardPackage';
  if (has('Ultimate')) return 'ultimateCollection';
  if (has('Gifts for Everyone Package')) return 'giftsForEveryone';
  if (has('The Keepsake Collection')) return 'keepsake';
  if (has('All Digital Package')) return 'allDigital';
  if (has('Picture Perfect Package')) return 'picturePerfect';
  if (has('School Days Package')) return 'schoolDays';
  if (has('Just an 8x10')) return 'justAn8x10';
  if (has('Single 8x10')) return 'justAn8x10'; // 2026-27 option naming ("Single 8x10 Print")
  if (has('Basic Package')) return 'basicPackage';

  return null;
}
